import {Component, EventEmitter, Optional, Output, ViewChild} from '@angular/core';
import {AbstractControl, FormControl, FormGroup, Validators} from '@angular/forms';
import {
    BimDataModel,
    BimDataModelCreateRequest,
    BimDataModelPhase,
    BimDataModelType,
    BimModelService,
    FileUploadComponent,
    getAllEnumValues,
    ProjectApiService
} from '@cas-hub/acabim-common-client';
import {NGXLogger} from 'ngx-logger';
import {MessageService} from 'primeng/api';

const enum BimModelCreationState {
    pending = 0,
    uploading = 1,
    processing = 2
}

const enum BimModelFileDatasource {
    ifc = 0
}

@Component({
    selector: 'acabim-admin-create-bim-model',
    templateUrl: './create-bim-model.component.html',
    styleUrls: ['./create-bim-model.component.scss']
})
export class CreateBimModelComponent {

    @Output() public bimModelCreated = new EventEmitter<BimDataModel>();

    @ViewChild('ifcFileUpload', {static: false}) public ifcFileUpload?: FileUploadComponent;

    public fileDataSource = BimModelFileDatasource.ifc;

    public currentUploadProgress = 0;
    public currentState = BimModelCreationState.pending;

    public validYearRange: string; // The range for the Practical Completion date and construction start date

    public currentModelTypes: { name: string, id: number }[] = [];
    public currentBimPhases: { name: string, id: number }[] = [
        {name: 'Pre Construction Model', id: BimDataModelPhase.PreConstruction},
        {name: 'Construction Model', id: BimDataModelPhase.Construction},
        {name: 'Post Construction Model', id: BimDataModelPhase.PostConstruction}
    ];

    public createBimModelForm: FormGroup;

    public get selectedModelPhase(): number {
        return (this.createBimModelForm.get('modelPhase')?.value as { name: string, id: number }).id;
    }

    public get isConstructionPhase(): boolean {
        return this.selectedModelPhase === BimDataModelPhase.Construction;
    }

    public get isPostConstructionPhase(): boolean {
        return this.selectedModelPhase === BimDataModelPhase.PostConstruction;
    }

    constructor(public readonly projectApiService: ProjectApiService,
                public readonly bimModelService: BimModelService,
                private messageService: MessageService,
                @Optional() private readonly logger?: NGXLogger) {
        for (const modelType of getAllEnumValues(BimDataModelType)) {
            this.currentModelTypes.push({name: modelType, id: Number(BimDataModelType[modelType as any])});
        }

        this.validYearRange = ((new Date()).getFullYear() - 100) + ':' + ((new Date()).getFullYear() + 10);
        this.createBimModelForm = new FormGroup({
            completionDate: new FormControl(),
            constructionDate: new FormControl(),
            modelName: new FormControl(),
            remarks: new FormControl(),
            modelType: new FormControl(this.currentModelTypes[0]),
            modelPhase: new FormControl(this.currentBimPhases[0])
        });
    }

    public addModel(): void {
        if (this.createBimModelForm.invalid) {
            this.showError('Unable to create model');
            this.logger?.error('[CreateBimModelComponent] Form Validation Error - Unable to create model', this.createBimModelForm.errors);
            return;
        }

        const uploadFile = this.getFile();
        if (!uploadFile) {
            this.showError('No model source file selected');
            return;
        }

        const projectId = this.projectApiService.selectedProjectId;
        if (!projectId) {
            return;
        }

        const request: BimDataModelCreateRequest = {
            projectId,
            file: uploadFile,
            modelType: this.createBimModelForm.get('modelType')?.value,
            modelPhase: this.selectedModelPhase,
            practicalCompletionDate: this.createBimModelForm.get('completionDate')?.value,
            constructionStartDate: this.createBimModelForm.get('constructionDate')?.value,
            modelName: this.createBimModelForm.get('modelName')?.value,
            remarks: this.createBimModelForm.get('remarks')?.value
        };

        this.logger?.debug('[CreateBimModelComponent] Creating new Model: Request', request);
        this.bimModelService.createBimModel(request, (model?: BimDataModel): void => {
            if (model) {
                this.bimModelCreated.emit(model);
            }
        }).subscribe((progress: number) => {
            this.currentState = progress < 99 ? BimModelCreationState.uploading : BimModelCreationState.processing;
            this.currentUploadProgress = progress;
        }, (error: any) => {
            this.currentState = BimModelCreationState.pending;
            this.showError(error);
            this.logger?.error('[CreateBimModelComponent] API Error when creating model', error);
        }, () => {
            this.currentState = BimModelCreationState.pending;
        });
    }

    // The two date fields in the form (namely `constructionDate` and the `completionDate` depends on the selected model phase
    public refreshDateValidators(): void {
        const resetAndClearFormControl = (control: AbstractControl | null): void => {
            control?.reset();
            control?.clearValidators();
            control?.updateValueAndValidity();
        };

        const constructionDateControl = this.createBimModelForm.get('constructionDate');
        const completionDateControl = this.createBimModelForm.get('completionDate');

        switch (this.selectedModelPhase) {
            case BimDataModelPhase.PostConstruction:
                completionDateControl?.setValidators(Validators.required);
                completionDateControl?.updateValueAndValidity();

                resetAndClearFormControl(constructionDateControl);
                break;

            case BimDataModelPhase.Construction:
                constructionDateControl?.setValidators(Validators.required);
                constructionDateControl?.updateValueAndValidity();

                resetAndClearFormControl(completionDateControl);
                break;

            default:
                resetAndClearFormControl(completionDateControl);
                resetAndClearFormControl(constructionDateControl);
        }
    }

    private getFile(): File | undefined {
        switch (this.fileDataSource) {
            case BimModelFileDatasource.ifc:
                return this.ifcFileUpload?.file;

            default:
                return undefined;
        }
    }

    private showError(error: string): void {
        this.messageService.clear();
        this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: error
        });
    }
}
